// @flow
import DashboardTableRow from "components/DashboardTableRow";
import usePermissions from "hooks/usePermissions";
import get from "lodash/get";
import isNil from "lodash/isNil";
import * as React from "react";
import type { Message } from "types/message";
import Dropdown from "ui/atoms/Dropdown";
import Icons from "ui/Icons";
import { momentLocal } from "utils/helpers";

import { MessageStatus } from "./MessageStatus";
type Props = {
  response: Message,
  fetchingResponses: boolean,
  archiveResponse: (responseId: string, isPacket: boolean) => Promise<void>,
  removingResponse: ?string,
};

function ResponseRow({
  response,
  fetchingResponses,
  archiveResponse,
  removingResponse,
}: Props): React.Node {
  const isPacket = !isNil(response?.packet_response);
  const responseKey = isPacket ? "packet_response" : "response";
  const responseId = get(response, [responseKey, "id"], null);
  const submitted_at = get(response, [responseKey, "submitted_at"], null);

  const getResponseName = React.useCallback(() => {
    if (isPacket) {
      return response?.packet_response?.packet?.name ?? "Packet name missing";
    }

    if (response?.response?.document) {
      return response?.response?.document.name ?? "Document name missing";
    }

    return response?.response?.form?.name ?? "Form name missing";
  }, [isPacket, response]);

  const getResponseLink = React.useCallback(() => {
    if (!response?.response?.document) {
      return `/response/${responseId}?response_type=${
        isPacket ? "packet" : "form"
      }`;
    }
    return `response/${response.response.id}`;
  }, [isPacket, response, responseId]);

  const { canArchiveResponse } = usePermissions();

  const status = React.useMemo(() => {
    if (response.message_type === "internal") {
      return "Internal";
    }

    return submitted_at
      ? momentLocal(submitted_at).format("MM/DD/YYYY")
      : "Not set";
  }, [response.message_type, submitted_at]);

  const isMessageInternal = response.message_type === "internal";

  return (
    <DashboardTableRow
      customer={response.customer}
      disabled={removingResponse === responseId}
      popup={
        canArchiveResponse ? (
          <Dropdown
            items={[
              {
                key: "archive",
                label: (
                  <button
                    className="block w-full text-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none transition duration-150 ease-in-out"
                    onClick={() => {
                      archiveResponse(responseId, isPacket);
                    }}
                  >
                    Archive
                  </button>
                ),
              },
            ]}
          >
            <button
              className={`flex m-auto items-center ${
                fetchingResponses
                  ? "text-gray-200"
                  : "text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
              }`}
            >
              <Icons.ChevronDown inheritColor size={4} />
            </button>
          </Dropdown>
        ) : null
      }
      resourceLink={getResponseLink()}
      resourceName={getResponseName()}
      status={
        <>
          {isMessageInternal ? (
            <MessageStatus type="internal">{status}</MessageStatus>
          ) : null}
          {!isMessageInternal ? (
            <MessageStatus type="received">{status}</MessageStatus>
          ) : null}
        </>
      }
    />
  );
}

export default ResponseRow;
